import {
	captureException, type Context, type ErrorEvent, type Scope, type StackFrame, withScope,
} from '@sentry/vue';
import { HTTPError } from 'ky';
import type { APIErrorCodes, AppErrorCodes, AuthErrorCodes } from '@/enums/ErrorCodes';

/* REFACOR ME: Sentry does not export this type, but maybe in future versions: */
export type Primitive = number | string | boolean | bigint | symbol | null | undefined;

type SentryTagParams = {
	key: string;
	value: Primitive;
};

type SentryContextParams = {
	key: string;
	value: Context;
};

type SentryErrorTrackerExtendParams = {
	transactionName?: string;
	tags?: SentryTagParams[];
	contexts?: SentryContextParams[];
};

type SentryErrorTrackerParams = {
	errorCode: AuthErrorCodes | AppErrorCodes | APIErrorCodes | string;
	cause?: HTTPError | Error | unknown;
	extend?: SentryErrorTrackerExtendParams;
};

type SentryHttpErrorTrackerParams = SentryErrorTrackerParams & {
	cause: HTTPError;
};

type SentryAppErrorTrackerParams = SentryErrorTrackerParams & {
	cause: Error;
};

type SentryUnknownErrorTrackerParams = SentryErrorTrackerParams & {
	cause: unknown;
};

const handleExtendingSentryEvent = (params: SentryErrorTrackerExtendParams, scope: Scope) => {
	if (params.transactionName) {
		scope.setTransactionName(params.transactionName);
	}
	if (params.tags) {
		params.tags.forEach((tagParam) => scope.setTag(tagParam.key, tagParam.value));
	}
	if (params.contexts) {
		params.contexts.forEach((contextParam) => scope.setContext(contextParam.key, contextParam.value));
	}
};

const trackSentryHttpError = (params: SentryHttpErrorTrackerParams): string => withScope((scope) => {
	if (params.extend) {
		handleExtendingSentryEvent(params.extend, scope);
	}

	scope.setTag('http_error_code', params.errorCode);
	scope.setTag('error_code', String(params.errorCode));

	scope.setFingerprint([
		'{{ default }}',
		'HTTPError',
		String(params.errorCode),
		params.cause.name,
		String(params.cause.response.status),
	]);

	scope.setContext('HTTPError Context', {
		name: params.cause.name,
		message: params.cause.message,
		stack: params.cause.stack ?? null,
		cause: params.cause.cause ?? null,
	});

	scope.setContext('HTTPError Response', {
		status: params.cause.response.status,
		statusText: params.cause.response.statusText,
		url: params.cause.response.url,
	});

	scope.setContext('HTTPError Request Options', {
		method: params.cause.request.method,
		url: params.cause.request.url,
	});

	return captureException(params.cause);
});

const trackSentryAppError = (params: SentryAppErrorTrackerParams): string => withScope((scope) => {
	if (params.extend) {
		handleExtendingSentryEvent(params.extend, scope);
	}

	scope.setTag('app_error_code', params.errorCode);
	scope.setTag('error_code', String(params.errorCode));

	scope.setFingerprint([
		'{{ default }}',
		'AppError',
		String(params.errorCode),
		params.cause.name,
	]);

	scope.setContext('AppError Context', {
		name: params.cause.name,
		message: params.cause.message,
		stack: params.cause.stack ?? null,
		cause: params.cause.cause ?? null,
	});

	return captureException(params.cause);
});

const trackSentryUnknownError = (params: SentryUnknownErrorTrackerParams): string => withScope((scope) => {
	if (params.extend) {
		handleExtendingSentryEvent(params.extend, scope);
	}

	scope.setTag('error_code', String(params.errorCode));

	scope.setFingerprint([
		'{{ default }}',
		'UnknownError',
		String(params.errorCode),
	]);

	if (params.cause === undefined || params.cause === null) {
		scope.setContext('Unknown Cause', { value: null });
		return captureException(new Error(`Unknown error (no cause) for code ${params.errorCode}`));
	}

	if (typeof params.cause === 'string') {
		scope.setContext('Unknown Cause', { value: params.cause });
		return captureException(new Error(`String error: ${params.cause}`));
	}

	// Fallback for objects / other primitives
	let serialized: string;
	try {
		serialized = JSON.stringify(params.cause, null, 2);
	} catch {
		serialized = '[Unserializable cause]';
	}

	scope.setContext('Unknown Cause', { serialized });
	return captureException(new Error(`Non-Error thrown for code ${params.errorCode}`));
});

export const trackSentryError = (params: SentryErrorTrackerParams): string => {
	if (params.cause instanceof HTTPError) {
		return trackSentryHttpError({
			cause: params.cause,
			errorCode: params.errorCode,
			extend: params.extend,
		});
	}

	if (params.cause instanceof Error) {
		return trackSentryAppError({
			cause: params.cause,
			errorCode: params.errorCode,
			extend: params.extend,
		});
	}

	return trackSentryUnknownError({
		cause: params.cause,
		errorCode: params.errorCode,
		extend: params.extend,
	});
};

const APP_CHUNK_PATTERN = /\/assets\/[^/]+\.js(\?|$)/;

const isAppChunkFrame = (frame: StackFrame): boolean => APP_CHUNK_PATTERN.test(frame.filename ?? frame.abs_path ?? '');

/* Google's injected iOS scripts throw RangeErrors with no frame of ours. See M2M-2801. */
export const isForeignRangeError = (event: ErrorEvent): boolean => {
	const exception = event.exception?.values?.[0];

	if (exception?.type !== 'RangeError') {
		return false;
	}

	const frames = exception.stacktrace?.frames ?? [];

	return !frames.some(isAppChunkFrame);
};
